// src/components/Investment/WithdrawalModal.jsx - PRODUCTION MODE + CURRENCY CONVERSION
import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../context/CurrencyContext';
import { investmentService } from '../../services/investmentService';
import { API_BASE_URL } from '../../utils/constants';
import './WithdrawalModal.css';

const WithdrawalModal = ({ investment, onClose, onSuccess }) => {
  const { formatCurrency, currency } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  
  useEffect(() => {
    fetchExchangeRate();
  }, []);

  const fetchExchangeRate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/currency/rate/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setExchangeRate(data);
      }
    } catch (err) {
      console.error('Error fetching exchange rate:', err);
    }
  };

  // Convert NGN to user's display currency
  const convertFromNGN = (ngnAmount) => {
    if (!ngnAmount) return 0;
    
    const amount = parseFloat(ngnAmount);
    
    if (currency === 'NGN' || !exchangeRate) {
      return amount;
    }
    
    // Convert NGN to USD
    const rate = parseFloat(exchangeRate.ngn_to_usd);
    return amount * rate;
  };

  // Format amount with proper conversion
  const formatAmount = (field) => {
    // First check if there's a display field from backend
    const displayField = `${field}_display`;
    
    if (investment[displayField] && investment[displayField].formatted) {
      return investment[displayField].formatted;
    }
    
    // Backend stores in NGN, convert if needed
    const amountInNGN = investment[field] || 0;
    const convertedAmount = convertFromNGN(amountInNGN);
    return formatCurrency(convertedAmount);
  };

  // Get stock current price with conversion
  const getStockPrice = () => {
    if (investment.stock?.current_price_display) {
      return investment.stock.current_price_display.formatted;
    }
    
    const priceInNGN = investment.stock?.current_price || 0;
    const convertedPrice = convertFromNGN(priceInNGN);
    return formatCurrency(convertedPrice);
  };

  // Get available options first
  const getAvailableWithdrawalOptions = () => {
    const options = [];
    
    console.log('🔍 DEBUG: Checking withdrawal options');
    console.log('- Withdrawal preference:', investment.withdrawal_preference);
    console.log('- Unrealized profit:', investment.unrealized_profit);
    console.log('- Profit withdrawal %:', investment.profit_withdrawal_percentage);
    
    const isMatured = investment.status === 'MATURED' || investment.is_matured === true;
    
    // For matured investments, ALWAYS offer full withdrawal
    if (isMatured) {
      options.push({ value: 'FULL', label: 'Withdraw Entire Investment' });
    }
    
    // Only add PROFIT option if withdrawal preference is PROFIT_PERIODIC and there's profit
    if (investment.withdrawal_preference === 'PROFIT_PERIODIC' && investment.unrealized_profit > 0) {
      const percentage = investment.profit_withdrawal_percentage || 100;
      options.push({ 
        value: 'PROFIT', 
        label: `Withdraw ${percentage}% of Profit` 
      });
    }
    
    // Only add FIXED option if withdrawal preference is FIXED_PERIODIC
    if (investment.withdrawal_preference === 'FIXED_PERIODIC') {
      options.push({ value: 'FIXED', label: 'Fixed Amount Withdrawal' });
    }
    
    // Only add PARTIAL option if appropriate
    if (investment.withdrawal_preference !== 'PROFIT_PERIODIC' && 
        investment.withdrawal_preference !== 'FIXED_PERIODIC' &&
        investment.quantity > investment.minimum_shares_to_keep) {
      options.push({ value: 'PARTIAL', label: 'Partial Withdrawal' });
    }
    
    console.log('📋 Available options:', options);
    return options;
  };

  const availableOptions = getAvailableWithdrawalOptions();
  
  const [withdrawalType, setWithdrawalType] = useState(
    availableOptions.length > 0 ? availableOptions[0].value : 'FULL'
  );
  const [sharesToSell, setSharesToSell] = useState('');

  // Update withdrawal type if options change
  useEffect(() => {
    if (availableOptions.length > 0 && !availableOptions.find(opt => opt.value === withdrawalType)) {
      setWithdrawalType(availableOptions[0].value);
    }
  }, [availableOptions, withdrawalType]);

  const calculatePartialWithdrawalAmount = () => {
    if (!sharesToSell || parseFloat(sharesToSell) <= 0) return 0;
    
    const shares = parseFloat(sharesToSell);
    const priceInNGN = parseFloat(investment.stock.current_price);
    const amountInNGN = shares * priceInNGN;
    
    // Convert to display currency
    return convertFromNGN(amountInNGN);
  };

  // Calculate profit withdrawal amount with conversion
  const calculateProfitWithdrawalAmount = () => {
    if (!investment.unrealized_profit) return 0;
    
    const percentage = investment.profit_withdrawal_percentage || 100;
    const profitInNGN = parseFloat(investment.unrealized_profit);
    const withdrawalInNGN = profitInNGN * (percentage / 100);
    
    // Convert to display currency
    return convertFromNGN(withdrawalInNGN);
  };

  // Get fixed withdrawal amount with conversion
  const getFixedWithdrawalAmount = () => {
    if (!investment.fixed_withdrawal_amount) return 0;
    
    const amountInNGN = parseFloat(investment.fixed_withdrawal_amount);
    return convertFromNGN(amountInNGN);
  };

  const handleWithdrawal = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('=== WITHDRAWAL DEBUG INFO ===');
      console.log('Investment status:', investment.status);
      console.log('Investment is_matured:', investment.is_matured);
      console.log('Withdrawal preference:', investment.withdrawal_preference);
      console.log('Can process withdrawal:', investment.can_process_withdrawal);
      console.log('Selected withdrawal type:', withdrawalType);
      console.log('Available options:', availableOptions);

      const withdrawalData = {
        investment_id: investment.id,
        withdrawal_type: withdrawalType
      };

      // Only add shares_to_sell for PARTIAL withdrawals
      if (withdrawalType === 'PARTIAL') {
        if (!sharesToSell || parseFloat(sharesToSell) <= 0) {
          setError('Please enter valid number of shares to sell');
          setLoading(false);
          return;
        }
        withdrawalData.shares_to_sell = parseFloat(sharesToSell);
      }

      console.log('Sending withdrawal data:', withdrawalData);

      const result = await investmentService.processWithdrawal(withdrawalData);
      console.log('Withdrawal API result:', result);

      if (result && (result.success === true || result.message)) {
        console.log('Withdrawal successful!');
        onSuccess();
      } else {
        const errorMsg = result?.error || 'Withdrawal failed - unknown response format';
        console.error('Withdrawal failed:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Withdrawal error caught:', err);
      setError(err.message || 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };

  // Show message if no withdrawal options are available
  if (availableOptions.length === 0) {
    return (
      <div className="withdrawal-modal-overlay" onClick={onClose}>
        <div className="withdrawal-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div>
              <h2>Withdraw Funds</h2>
              {currency && (
                <span className="currency-badge">{currency}</span>
              )}
            </div>
            <button className="close-button" onClick={onClose}>×</button>
          </div>

          <div className="error-message">
            No withdrawal options available for this investment at this time.
          </div>

          <div className="modal-actions">
            <button className="cancel-button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="withdrawal-modal-overlay" onClick={onClose}>
      <div className="withdrawal-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2>Withdraw Funds</h2>
            {currency && (
              <span className="currency-badge">{currency}</span>
            )}
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {/* Investment Info */}
        <div className="investment-info">
          <h3>{investment.stock?.symbol} - {investment.stock?.name}</h3>
          <div className="info-grid">
            <div className="info-item">
              <span>Current Value:</span>
              <span>{formatAmount('current_value')}</span>
            </div>
            <div className="info-item">
              <span>Available Shares:</span>
              <span>{parseFloat(investment.quantity).toFixed(6)}</span>
            </div>
            <div className="info-item">
              <span>Current Price:</span>
              <span>{getStockPrice()}</span>
            </div>
            {investment.unrealized_profit > 0 && (
              <div className="info-item">
                <span>Available Profit:</span>
                <span className="profit">
                  {formatAmount('unrealized_profit')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Withdrawal Type Selection */}
        <div className="withdrawal-type">
          <label>Select Withdrawal Type:</label>
          <div className="type-options">
            {availableOptions.map(option => (
              <button
                key={option.value}
                className={`type-option ${withdrawalType === option.value ? 'selected' : ''}`}
                onClick={() => setWithdrawalType(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* PARTIAL Withdrawal Input */}
        {withdrawalType === 'PARTIAL' && (
          <div className="partial-withdrawal">
            <label>Number of Shares to Sell:</label>
            <input
              type="number"
              step="0.000001"
              min="0.000001"
              max={investment.quantity - investment.minimum_shares_to_keep}
              value={sharesToSell}
              onChange={(e) => setSharesToSell(e.target.value)}
              placeholder={`Max: ${(investment.quantity - investment.minimum_shares_to_keep).toFixed(6)}`}
            />
            {sharesToSell && (
              <div className="calculated-amount">
                Estimated amount: {formatCurrency(calculatePartialWithdrawalAmount())}
              </div>
            )}
          </div>
        )}

        {/* PROFIT Withdrawal Info - PRODUCTION MODE */}
        {withdrawalType === 'PROFIT' && investment.profit_withdrawal_percentage && (
          <div className="profit-withdrawal-info">
            <p>You will withdraw <strong>{investment.profit_withdrawal_percentage}%</strong> of available profit</p>
            {investment.unrealized_profit > 0 && (
              <div className="calculated-amount">
                Amount: {formatCurrency(calculateProfitWithdrawalAmount())}
                <small> (Auto-calculated based on {investment.profit_withdrawal_percentage}% of {formatAmount('unrealized_profit')})</small>
              </div>
            )}
          </div>
        )}

        {/* FIXED Withdrawal Info - PRODUCTION MODE */}
        {withdrawalType === 'FIXED' && investment.fixed_withdrawal_amount && (
          <div className="fixed-withdrawal-info">
            <p>Fixed withdrawal amount: <strong>{formatCurrency(getFixedWithdrawalAmount())}</strong></p>
            <div className="calculated-amount">
              <small>Auto-processed every 4 weeks</small>
            </div>
          </div>
        )}

        {/* Full Withdrawal Info */}
        {withdrawalType === 'FULL' && (
          <div className="full-withdrawal-info">
            <p>⚠️ You will withdraw the <strong>entire investment</strong></p>
            <p className="estimated-amount">
              Total amount: {formatAmount('current_value')}
            </p>
            {investment.profit_loss && (
              <p className={`profit-loss ${parseFloat(investment.profit_loss) >= 0 ? 'positive' : 'negative'}`}>
                {parseFloat(investment.profit_loss) >= 0 ? 'Profit' : 'Loss'}: {formatAmount('profit_loss')} 
                ({investment.profit_loss_percentage}%)
              </p>
            )}
          </div>
        )}

        {/* Exchange Rate Info */}
        {exchangeRate && currency === 'USD' && (
          <div className="exchange-rate-display">
            <small>
              💱 Current Rate: 1 USD = ₦{parseFloat(exchangeRate.usd_to_ngn).toFixed(2)}
              <br/>
              <span className="rate-source">Source: CoinGecko</span>
            </small>
          </div>
        )}

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Action Buttons */}
        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button 
            className="confirm-button" 
            onClick={handleWithdrawal}
            disabled={loading || 
              (withdrawalType === 'PARTIAL' && !sharesToSell)
            }
          >
            {loading ? 'Processing...' : 'Confirm Withdrawal'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalModal;